import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ConfirmOptions = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
};

type ConfirmRequest = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

type ConfirmContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm() {
  const value = useContext(ConfirmContext);
  if (!value) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return value.confirm;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setRequest({ ...options, resolve });
    });
  }, []);

  const close = useCallback((result: boolean) => {
    setRequest((current) => {
      if (current) current.resolve(result);
      return null;
    });
  }, []);

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {request ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={request.title}
        >
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
            onClick={() => close(false)}
            aria-label="Fechar"
          />
          <div className="relative z-10 w-full max-w-md rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h3 className="text-lg font-black text-slate-900 tracking-tight">{request.title}</h3>
              {request.description ? (
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">{request.description}</p>
              ) : null}
            </div>
            <div className="p-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button
                type="button"
                onClick={() => close(false)}
                className="px-5 py-3 rounded-2xl bg-slate-100 text-slate-700 font-black text-[11px] uppercase tracking-widest hover:bg-slate-200 transition-colors"
              >
                {request.cancelText || 'Cancelar'}
              </button>
              <button
                type="button"
                onClick={() => close(true)}
                className={`px-5 py-3 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-colors ${
                  request.danger
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-brand-green text-white hover:bg-brand-darkGreen'
                }`}
              >
                {request.confirmText || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </ConfirmContext.Provider>
  );
}

